"use client";

import { Avatar, Box, Link, ListSubheader, MenuItem, Typography, LinearProgress, Paper, Stack, Divider, ListItemIcon, ListItemText, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import '@/style/css/Profil.css';
import { useContext } from 'react';
import TokenContext from '@/context/TokenProvider';
import { useRouter } from 'next/navigation';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import BookmarksRoundedIcon from '@mui/icons-material/BookmarksRounded';

import jwtDecode, { JwtPayload } from "jwt-decode";
import { useProfile } from "@/utils/Hooks";
import { displayDate } from "@/utils/dateDisplay";

interface ProfilScreenContentsProps {
  handleClose: () => void;
  isOnAdminDashboard?: boolean;
}

const ProfileMenuContents = (props: ProfilScreenContentsProps) => {
  const { authenticatedFetcher, accessToken, resetAuthTokens } = useContext(TokenContext)!;
  const router = useRouter();
  const theme = useTheme();

  const { data: profile } = useProfile(authenticatedFetcher);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      props.handleClose();
      resetAuthTokens();
      router.push('/');
    } catch (error: any) {
      if (error.response) {
        console.error(error.response);
      }
    }
  };

  const decodedData = accessToken ? jwtDecode<JwtPayload & { role: string }>(accessToken) : null;

  return (
    <Box sx={{ width: '100%', outline: 'none' }}>
      
      {profile && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <Avatar
              sx={{ width: 56, height: 56, border: `2px solid ${theme.palette.primary.main}` }}
              alt={profile.nama || profile.username}
              src={profile.foto ? `${process.env.NEXT_PUBLIC_API_URL}/${profile.foto}` : undefined}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                {profile.nama || profile.username}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: -0.25 }}>
                {profile.email || profile.username}
              </Typography>
              <Chip size="small" color="primary" variant="outlined" label={decodedData?.role === 'admin' ? "Admin" : "Pengguna"} sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
            </Box>
          </Box>
          <Divider />

          <Box sx={{ p: 2, pb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" mb={0.75} alignItems="center">
              <Typography variant="body2" fontWeight="600" color="text.secondary">
                Kuota Baca
              </Typography>
              <Typography variant="caption" fontWeight="bold" color="primary.main">
                {profile.kuotaBaca?.available ?? 0} / {profile.kuotaBaca?.allowed ?? 0}
              </Typography>
            </Stack>
            <LinearProgress 
              variant="determinate" 
              value={profile.kuotaBaca ? ((profile.kuotaBaca.available) / profile.kuotaBaca.allowed) * 100 : 0} 
              sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1) }} 
            />
          </Box>
        </>
      )}

      {profile?.kunjungan && (
        <Box sx={{ p: 2, pt: 0.5, pb: 1.5 }}>
          <Typography variant="overline" color="text.secondary" fontWeight="700" sx={{ lineHeight: 2, letterSpacing: 0.5 }}>
            Kunjungan Aktif
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, backgroundColor: alpha(theme.palette.info.main, 0.05), borderColor: alpha(theme.palette.info.main, 0.2) }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Waktu Masuk</Typography>
                <Typography variant="caption" fontWeight="600">{displayDate(profile.kunjungan.waktuMasuk)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Loker</Typography>
                <Typography variant="caption" fontWeight="600">{profile.kunjungan.loker || "Tidak dipinjam"}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Tas</Typography>
                <Typography variant="caption" fontWeight="600">{profile.kunjungan.tas || "Tidak dipinjam"}</Typography>
              </Stack>
            </Stack>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', lineHeight: 1.2 }}>
            * Scan kembali QR Code di layar monitor perpus untuk mengakhiri kunjungan.
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '2em', pt: 0.5 }}>Navigasi Cepat</ListSubheader>

      {decodedData?.role === 'admin' && (
        <MenuItem
          onClick={() => props.handleClose()}
          component={Link}
          href={props.isOnAdminDashboard ? '/' : '/admin'}
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            {props.isOnAdminDashboard ? <HomeRoundedIcon fontSize="small" color="primary" /> : <AdminPanelSettingsRoundedIcon fontSize="small" color="primary"/>}
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
            {props.isOnAdminDashboard ? "Beranda Publik" : "Dasbor Administrasi"}
          </ListItemText>
          <ArrowForwardIosRoundedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
        </MenuItem>
      )}

      <MenuItem onClick={() => props.handleClose()} component={Link} href={'/profil-saya'} sx={{ py: 1.5 }}>
        <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Profil Akun</ListItemText>
        <ArrowForwardIosRoundedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
      </MenuItem>
      
      <MenuItem onClick={() => props.handleClose()} component={Link} href={'/riwayat-pinjaman'} sx={{ py: 1.5 }}>
        <ListItemIcon><HistoryRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Riwayat Pinjaman</ListItemText>
        <ArrowForwardIosRoundedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
      </MenuItem>

      <MenuItem onClick={() => props.handleClose()} component={Link} href={'/riwayat-baca'} sx={{ py: 1.5 }}>
        <ListItemIcon><AutoStoriesRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Riwayat Baca</ListItemText>
        <ArrowForwardIosRoundedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
      </MenuItem>

      <MenuItem onClick={() => props.handleClose()} component={Link} href={'/penandaan-koleksi'} sx={{ py: 1.5 }}>
        <ListItemIcon><BookmarksRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Penandaan Koleksi</ListItemText>
        <ArrowForwardIosRoundedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
        <ListItemIcon sx={{ color: 'inherit' }}>
          <LogoutRoundedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Keluar Akun</ListItemText>
      </MenuItem>
    </Box>
  );
};

export default ProfileMenuContents;
